import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Box, Typography, TextField, Grid, Button, IconButton, Skeleton, useMediaQuery } from '@mui/material';
import { FaEye, FaEyeSlash, FaTwitter, FaInstagram, FaLinkedin, FaYoutube, FaFacebook, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import ReCAPTCHA from "react-google-recaptcha";
import 'react-toastify/dist/ReactToastify.css';
import { ShimmerSimpleGallery } from "react-shimmer-effects";
import behaves from "../../../../assets/learnersSignup.webp";
import Headrepage from "../../../mainpage/Headerpages/learnersHederpage";
import { LearnersSignup, LearnersVerifyCaptcha } from "../../../../helper/base_url";
import Footer from "../../../mainpage/learnersFooter";
import "../../../../styles/footer.css";
import useNetworkStatus from "../../../../helper/NetworkStatus";

const regex = {
  name: /^[A-Za-z\s]+$/,
  email: /^(?!.*\.\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/i,
  password: /^(?=.*\d)(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[a-z])(?!.*\s).{8,}$/,
};

const Signup = () => {
  const [data, setData] = useState({
    name: "", email: "", username: "", password: "", confirmPassword: "", role: "student"
  });
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const recaptchaRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const { isOnline } = useNetworkStatus();
  const [captchaStatus, setCaptchaStatus] = useState(null);
  const navigate = useNavigate();

  const isMobile = useMediaQuery('(max-inline-size:600px)');
  const isTablet = useMediaQuery('(min-inline-size:601px) and (max-inline-size:960px)');

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      setTimeout(() => navigate("/learners/dashboardpage"), 1000);
    } else {
      setCheckingToken(false);
    }
  }, [navigate]);

  const validateField = (name, value) => {
    if (!value.trim()) return `${name[0].toUpperCase() + name.slice(1)} is required.`;

    if (name === "name" && !regex.name.test(value)) return "Name must contain only letters.";
    if (name === "email") {
      const emailValue = value.toLowerCase();

      if (!regex.email.test(emailValue)) return "Invalid email format.";
      if (emailValue.split("@")[0].length < 3)
        return "Email must have at least 3 characters before @.";
    }

    if (name === "password" && !regex.password.test(value))
      return "Must include at least 8 characters, with uppercase, lowercase, and a special character.";
    if (name === "confirmPassword" && value !== data.password)
      return "Passwords do not match.";

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // ✅ Convert email to lowercase automatically
    const updatedValue = name === "email" ? value.toLowerCase() : value;

    setData(prev => ({ ...prev, [name]: updatedValue }));

    const error = validateField(name, updatedValue);
    setErrors(prev => ({ ...prev, [name]: error }));
  };


  // reCAPTCHA callback
  const handleCaptchaChange = async (token) => {
    setCaptchaToken(token);
    try {
      const res = await axios.post(`${LearnersVerifyCaptcha}`, { captchaToken: token });
      if (res.status === 200) {
        setCaptchaVerified(true);
       setCaptchaStatus("success");
      }
    } catch (err) {
      setCaptchaVerified(false);
      setCaptchaStatus("failed");
      recaptchaRef.current?.reset();
      setCaptchaToken("");
    }
  };

  const validateForm = () => {
    const newErrors = {};
    for (let key in data) {
      if (["username", "role"].includes(key)) continue;
      const error = validateField(key, data[key]);
      if (error) newErrors[key] = error;
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    setErrors(validationErrors);

     if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fix the errors before submitting.");
      return;
    }

    if (!captchaToken) {
      toast.error("Please complete the reCAPTCHA.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(LearnersSignup, { ...data, captchaToken }, {
        headers: { "Content-Type": "application/json" },
      });
      const { message, error } = res.data;
      error ? toast.error(message || "Account already exists")
            : toast.success("Registration successful!");

      if (!error) setTimeout(() => navigate("/learners/signin"), 1500);
    } catch (err) {
      toast.error(err.response?.data?.message || "Network error, please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (checkingToken) {
    return (
      <Box sx={{
        height : "100vh", width : "100%", display: "flex",
        justifyContent: "center", alignItems: "center",
        bgcolor: "#f5f7fa", padding: "20px"
      }}>
        <Box sx={{
          width : { xs: "90%", sm: "70%", md: "50%", lg: "40%" },
          padding: 3, borderRadius: 3, boxShadow: 3, bgcolor: "#ffffff"
        }}>
          <Skeleton variant="text" height={50} width="80%" sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 3 }} />
          <Skeleton variant="rounded" height={40} width="100%" sx={{ mb: 3 }} />
          <Skeleton variant="text" width="60%" />
        </Box>
      </Box>
    );
  }

  return (
    <div className="students-flex">
      <Headrepage />
       {isOnline ? (
      <div className="homepagecolores">
       <div style={{ width : "100%", overflowX: "hidden", overflowY: "auto" }}>
        <ToastContainer position="top-center" />
        <Grid container justifyContent="center" alignItems="center" sx={{ p: { xs: "20px 20px", md: "30px 20px" }, gap: 5 }} >
            {/* Left Image */}
            {!isMobile && !isTablet && (
              <Grid item xs={12} sm={8} md={6} lg={4} display="flex" justifyContent="center" sx={{ position: "relative" }}>
                <Box component="img" src={behaves} alt="Signup"
                  sx={{
                    width : "100%",
                    maxWidth : 650,
                    borderRadius: 3,
                    // boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                    // transform: "scale(1)",
                    // transition: "transform 0.3s ease",
                    // "&:hover": {
                    //   transform: "scale(1.05)",
                    // },
                  }}
                />
              </Grid>
            )}
            {/* Signup Form */}
            <Grid item xs={12} sm={8} md={6} lg={4} sx={{ p: { xs: 3, md: 2 }, display: "flex", flexDirection: "column", alignItems: "center" }} >
             <Typography
              variant="h5"
              align="center"
              gutterBottom
              sx={{ color: "#043885", fontWeight: 600, fontSize: { xs: "24px", sm: "28px" }, mb: 3 }}
            >
              Sign Up to your account
            </Typography> 
            {/* Create Your Account */}
              <form onSubmit={handleSubmit} style={{ width : "100%" }}>
                {/** Name Field */}
                <TextField
                  label="Full Name"
                  name="name"
                  fullWidth
                  margin="normal"
                  value={data.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={loading}
                  required
                  placeholder="Enter your full name"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      fontWeight: 700,
                      color: "black",
                      "& .MuiFormLabel-asterisk": {
                        color: data.name ? "blue" : "red",
                      },
                    },
                  }}
                  sx={{
                    backgroundColor: "#f9f9f9",
                    borderRadius: 1,
                  }}
                />

                {/** Email Field */}
                <TextField
                  label="Email Address"
                  name="email"
                  fullWidth
                  margin="normal"
                  value={data.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={loading}
                  required
                  placeholder="Enter your email"
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 700, color: "black", "& .MuiFormLabel-asterisk": { color: data.email ? "blue" : "red", } },
                  }}
                  sx={{
                    backgroundColor: "#f9f9f9",
                    borderRadius: 1,
                  }}
                />

                {/** Password Field */}
                <Box position="relative">
                  <TextField
                    label="Password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    margin="normal"
                    value={data.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    helperText={errors.password}
                    disabled={loading}
                    required
                    placeholder="Enter your password"
                    InputLabelProps={{
                      shrink: true,
                      sx: { fontWeight: 700, color: "black", "& .MuiFormLabel-asterisk": { color: data.password ? "blue" : "red", } },
                    }}
                    sx={{ backgroundColor: "#f9f9f9", borderRadius: 1 }}
                  />
                  <IconButton onClick={() => setShowPassword(prev => !prev)}
                    style={{ position: "absolute", right : 10, top : "28%" }}>
                    {showPassword ? <FaEye /> : <FaEyeSlash />}
                  </IconButton>
                </Box>

                {/** Confirm Password */}
                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  fullWidth
                  margin="normal"
                  value={data.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  disabled={loading}
                  required
                  placeholder="Re-enter your password"
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 700, color: "black", "& .MuiFormLabel-asterisk": { color: data.confirmPassword ? "blue" : "red", } },
                  }}
                  sx={{ backgroundColor: "#f9f9f9", borderRadius: 1 }}
                />

                {/** Captcha */}
                <div style={{ margin: "20px 0px" }}>
                  <ReCAPTCHA
                    sitekey="6Le83KcqAAAAAInqCfWquiIGanUjpu2RxTMgKwuJ"
                    onChange={handleCaptchaChange}
                    ref={recaptchaRef}
                  />
                  {captchaStatus === "success" && (
                    <p style={{ color: "green", fontSize: "14px", marginTop : "8px", display: "flex", alignItems: "center", gap: "5px" }}>
                      <FaCheckCircle /> Verified successfully
                    </p>
                  )}

                  {captchaStatus === "failed" && (
                    <p style={{ color: "red", fontSize: "14px", marginTop : "8px", display: "flex", alignItems: "center", gap: "5px" }}>
                      <FaTimesCircle /> Verification failed. Try again.
                    </p>
                  )}
                </div>

                {/** Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || !captchaVerified}
                  sx={{
                    py: 1.8,
                    fontWeight: 700,
                    fontSize: "16px",
                    backgroundColor: "rgba(27, 110, 244, 1)",
                    "&:hover": { backgroundColor: "rgba(0, 98, 255, 1)" },
                  }}
                >
                  {loading ? "Processing..." : "Sign Up"}
                </Button>
              </form>
              <Typography align="center" sx={{ mt: 3, fontSize: "14px", color: "#555" }}>
                Already have an account? <Link to="/learners/signin" style={{ color: "#043885", fontWeight: 600 }}>Log in</Link>
              </Typography>
            </Grid>
          </Grid>
          <div className="homepage-footers-images">
               <Footer />
            <footer className="homepage-footers">
            <hr className='homepage-footer-hr'/>
            <div className="learners-footers-seokeywords">
            </div>
              <div className="footer-social">
              <div className="homepage-footer-copyright">
                <p>© Huebits[2025].All Rights Reserved.</p>
              </div>
              <div className='footer-social-icons'>
                  <Link to="https://www.linkedin.com/company/huebits/mycompany" target="_blank" rel="noopener noreferrer"><FaLinkedin  className="social-icon"/></Link>
                  <Link to="https://x.com/HuebitsTech" target="_blank" rel="noopener noreferrer"><FaTwitter className="social-icon" /></Link>
                  <Link to="https://www.instagram.com/huebits_tech/" target="_blank" rel="noopener noreferrer"><FaInstagram className="social-icon" /></Link>
                  <Link to="https://www.youtube.com/@HuebitsTech" target="_blank" rel="noopener noreferrer"><FaYoutube className="social-icon" /></Link>
                  <Link to="https://www.facebook.com/profile.php?id=61571753805069" target="_blank" rel="noopener noreferrer"><FaFacebook className="social-icon" /></Link>
            </div>
            </div>
            </footer>
           </div>
         </div>
        </div>
         ) : (
              <div>
                  <ShimmerSimpleGallery card imageHeight={180} row={3} col={3} gap={20} />
              </div>
          )}
    </div>
  );
};

export default Signup;